import { Theme, alpha } from "@mui/material";
import { HeatMix, SummaryData } from "src/store/api/generatedApi";
import { NumberSerialiserOptions } from "hooks/serialisers/types";
import { roundToDecimalPlaces } from "hooks/serialisers/numbers";

const isYieldMetric = (summaryName: string): boolean => {
  return summaryName.toLowerCase().includes("yield");
};

export const calculateDiff = (summary: SummaryData | undefined): number => {
  return roundToDecimalPlaces(summary?.diff ?? 0, summary?.decimal_places ?? 0);
};

const isGoodChange = (diff: number, summaryName: string): boolean => {
  const isYield = isYieldMetric(summaryName);
  const isPositive = diff > 0;
  return isYield ? isPositive : !isPositive;
};

export const getBaseColor = (
  diff: number,
  summaryName: string,
  theme: Theme
): string => {
  if (diff === 0) {
    return theme.palette.text.primary;
  }

  const isGood = isGoodChange(diff, summaryName);
  return isGood ? theme.palette.success.main : theme.palette.error.main;
};

export const calculateOpacity = (
  diff: number,
  value: number | null
): number => {
  let opacity = 0.4;
  if (value !== null && value !== 0) {
    const ratio = Math.abs(diff) / Math.abs(value);
    opacity = Math.max(0.4, Math.min(1, ratio * 15));
  }
  return opacity;
};

export const calculateIntensity = (diff: number, maxDiff: number): number => {
  return Math.min(Math.abs(diff) / maxDiff, 1);
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result?.[1] || !result[2] || !result[3]) {
    throw new Error(`Invalid colour: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

const extractRgb = (rgba: string): { r: number; g: number; b: number } => {
  // support rgb and rgba
  const result = /^rgba?\((\d+), (\d+), (\d+),? ?(\d+\.\d+)?\)$/.exec(rgba);
  if (result?.[1] && result[2] && result[3]) {
    return {
      r: parseInt(result[1], 10),
      g: parseInt(result[2], 10),
      b: parseInt(result[3], 10),
    };
  }
  throw new Error(`Invalid colour: ${rgba}`);
};

const colourToRgb = (colour: string): { r: number; g: number; b: number } => {
  if (colour.startsWith("#")) {
    return hexToRgb(colour);
  }
  if (colour.startsWith("rgba") || colour.startsWith("rgb")) {
    return extractRgb(colour);
  }
  throw new Error(`Invalid colour: ${colour}`);
};

/**
 * Blends two RGB colors together based on an intensity factor, moving from base to target.
 */
const blendColors = (
  baseColor: { r: number; g: number; b: number },
  targetColor: { r: number; g: number; b: number },
  intensity: number
): string => {
  const red = Math.round(
    baseColor.r + intensity * (targetColor.r - baseColor.r)
  );
  const green = Math.round(
    baseColor.g + intensity * (targetColor.g - baseColor.g)
  );
  const blue = Math.round(
    baseColor.b + intensity * (targetColor.b - baseColor.b)
  );

  return `rgb(${red}, ${green}, ${blue})`;
};

const generateOpaqueRgbColor = (
  intensity: number,
  isPositive: boolean,
  theme: Theme
): string => {
  const targetColor = isPositive
    ? colourToRgb(theme.palette.success.main)
    : colourToRgb(theme.palette.error.main);
  const baseRgb = colourToRgb(theme.palette.text.primary);

  return blendColors(baseRgb, targetColor, intensity);
};

export const getDiffColor = (
  diff: number,
  value: number | null,
  summaryName: string,
  theme: Theme,
  forceOpacity?: number
): string => {
  if (diff === 0) {
    return alpha(theme.palette.text.primary, 0.4);
  }

  const baseColor = getBaseColor(diff, summaryName, theme);
  const opacity = forceOpacity ?? calculateOpacity(diff, value);

  return alpha(baseColor, opacity);
};

export const formatDiffValue = (
  diff: number,
  formatNumber: (value: number, options?: NumberSerialiserOptions) => string,
  decimalPlaces?: number
): string => {
  const formattedDiff = formatNumber(Math.abs(diff), {
    decimalPlaces: decimalPlaces ?? undefined,
  });

  if (diff === 0) {
    return `±${formattedDiff}`;
  }
  return diff > 0 ? `+${formattedDiff}` : `-${formattedDiff}`;
};

export const formatBaseValue = (
  summary: SummaryData,
  formatNumber: (value: number, options?: NumberSerialiserOptions) => string
): string => {
  return summary.value === null
    ? "-"
    : formatNumber(summary.value, {
        decimalPlaces: summary.decimal_places ?? undefined,
      });
};

export const getSourceValue = (
  source: string,
  mixes: HeatMix[],
  summaryName: string,
  ignoredMaterials: Set<string>
) => {
  const mix = mixes.find(
    (mix) =>
      mix.source === source &&
      equalSets(new Set(mix.ignored_materials), ignoredMaterials)
  );
  if (!mix) return null;
  const summary = mix.summary_data.find(
    (summary) => summary.name === summaryName
  );
  return {
    value: summary?.value ?? 0,
    decimalPlaces: summary?.decimal_places ?? 0,
  };
};

/**
 * Calculates an opaque color based on difference between actual and planned values.
 * Returns RGB color string - green for positive differences, red for negative.
 */
export const getOpaqueDiffColor = (
  actual: number | null,
  planned: number | null,
  threshold: number,
  maxDiff: number,
  theme: Theme,
  ignored?: boolean
): string => {
  if (ignored) {
    return theme.palette.grey[200];
  }
  if (actual == null || planned == null) {
    return generateOpaqueRgbColor(0, false, theme);
  }

  const diff = actual - planned;

  if (Math.abs(diff) < threshold) {
    return generateOpaqueRgbColor(0, false, theme);
  }

  const intensity = calculateIntensity(diff, maxDiff);
  return generateOpaqueRgbColor(intensity, diff > 0, theme);
};
/**
 * Calculates a color based on the relative percentage difference between actual and planned values.
 * Uses column-relative scaling where the color intensity is normalized across the min/max differences
 * in the dataset. Returns an RGB color string that transitions from white (low difference) to red
 * (high difference).
 */
export const getRelativeDiffColour = ({
  actual,
  planned,
  lowThreshold,
  spreadThreshold,
  theme,
  minDiff,
  maxDiff,
  intensityMultiplier = 0.9,
  minIntensityRange = 0.3,
  maxIntensityRange = 0.65,
}: {
  actual: number | null;
  planned: number | null;
  lowThreshold: number;
  spreadThreshold: number;
  theme: Theme;
  minDiff: number;
  maxDiff: number;
  intensityMultiplier?: number;
  minIntensityRange?: number;
  maxIntensityRange?: number;
}): string => {
  const white = { r: 255, g: 255, b: 255 };

  if (actual == null || planned == null) {
    return "rgb(255, 255, 255)";
  }

  let relativeDiff: number;
  if (planned === 0) {
    relativeDiff = actual === 0 ? 0 : 100;
  } else {
    relativeDiff = Math.abs((actual - planned) / planned) * 100;
  }

  if (relativeDiff <= lowThreshold) {
    return "rgb(255, 255, 255)";
  }

  const redColor = colourToRgb(theme.palette.error.main);
  const spread = maxDiff - minDiff;
  const normalizedDiff = (relativeDiff - minDiff) / spread;

  const spreadRatio = Math.max(spread / spreadThreshold, 1);
  const intensityRange =
    minIntensityRange + spreadRatio * (maxIntensityRange - minIntensityRange);
  const intensity = 0.3 + normalizedDiff * intensityRange;

  let r = Math.round(white.r + intensity * (redColor.r - white.r));
  let g = Math.round(white.g + intensity * (redColor.g - white.g));
  let b = Math.round(white.b + intensity * (redColor.b - white.b));

  r = Math.round(white.r + (r - white.r) * intensityMultiplier);
  g = Math.round(white.g + (g - white.g) * intensityMultiplier);
  b = Math.round(white.b + (b - white.b) * intensityMultiplier);

  return `rgb(${r}, ${g}, ${b})`;
};

export const equalSets = (a?: Set<string>, b?: Set<string>): boolean => {
  if (a === undefined || b === undefined) {
    return false;
  }
  const bMissingValueFromA = [...a].some((val) => ![...b].includes(val));
  const aMissingValueFromB = [...b].some((val) => ![...a].includes(val));
  return !(bMissingValueFromA || aMissingValueFromB);
};
